import collaboratorsService from '@/services/collaborators';
import areasService from '@/services/areas';
import salesService from '@/services/sales';
import salesMultiCustomerService from '@/services/salesMultiCustomer';
import servicesService from '@/services/services';
import tagsService from '@/services/tags';
import quotesService from '@/services/quotes';
import taxesService from '@/services/taxes';
import generalCatalogsService from '@/services/generalCatalogs';
import { resolveOption, toPositiveOptionId } from '@/utils/optionValues';
import { extractPayload, toBooleanFlag } from '@/utils/dataNormalization';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import { buildTodayDate } from '@/utils/datePresentation';
import {
  createInitialSaleForm,
  createInitialSaleOptions,
  applyDefaultStatus,
  syncAgencyAmount as syncSaleAgencyAmount,
  buildSalePayload,
} from '@/composables/sales/modal/useSaleModalForm';
import { fetchSaleModalCatalogs } from '@/composables/sales/modal/useSaleModalCatalogs';
import {
  buildFirstInstallmentOptions,
  createInitialPaymentPlanTaxPreview,
  buildPaymentPlanTaxPreviewPayload,
} from '@/composables/paymentPlans/usePaymentPlanModalForm';
import { applyFrequencyRules } from '@/composables/paymentPlans/usePaymentPlanGeneralTransitions';
import {
  createInitialSaleMultiCustomerForm,
  createInitialSaleMultiCustomerOptions,
  applyDefaultStatus as applyDefaultStatusMulti,
  syncAgencyAmount as syncSaleMultiCustomerAgencyAmount,
  buildSaleMultiCustomerPayload,
} from '@/composables/salesMultiCustomer/useSaleMultiCustomerModalForm';
import { fetchSaleMultiCustomerModalCatalogs } from '@/composables/salesMultiCustomer/useSaleMultiCustomerModalCatalogs';

const resolveCollectionRows = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  return Array.isArray(payload?.data) ? payload.data : [];
};

export const saleModalMethods = {
  resolveSectionKey(sectionKey) {
    const validKeys = this.sections.map((section) => section.key);
    const normalized = String(sectionKey || '').trim().toLowerCase();

    if (normalized === 'comments' || normalized === 'scope') {
      return 'details';
    }

    return validKeys.includes(normalized) ? normalized : this.sections[0].key;
  },
  applyInitialDataToForm() {
    if (this.isMultiCustomerMode) {
      this.applyInitialDataToFormMulti();
      return;
    }

    const initial = this.initialData && typeof this.initialData === 'object' ? this.initialData : {};
    const quoteExtrasRaw = Array.isArray(initial?.quote_extras) ? initial.quote_extras : [];

    const quoteMode = Number(initial?.quote_is_file || 0) === 1 ? 'file' : 'url';
    const quoteUrl = quoteMode === 'url' ? String(initial?.quote_document || '').trim() : '';
    const periodicityCode = String(initial?.periodicity_code || '').trim().toLowerCase();
    const firstInstallmentValue = String(initial?.first_installment || '').trim();
    const frequency = String(initial?.frequency || 'unique').trim().toLowerCase() || 'unique';
    const taxGroupId = Number(initial?.tax_group_id || 0) || 0;
    const taxId = Number(initial?.tax_id || initial?.taxes?.[0]?.id || 0) || 0;
    const hasExplicitWithoutTaxes = Object.prototype.hasOwnProperty.call(initial, 'without_taxes')
      || Object.prototype.hasOwnProperty.call(initial, 'withoutTaxes');
    const explicitWithoutTaxesValue = hasExplicitWithoutTaxes
      ? toBooleanFlag(initial?.without_taxes ?? initial?.withoutTaxes)
      : null;
    const hasTaxPayload = taxGroupId > 0 || taxId > 0;
    const resolvedWithoutTaxes = hasExplicitWithoutTaxes
      ? explicitWithoutTaxesValue
      : (hasTaxPayload ? taxGroupId <= 0 && taxId <= 0 : Boolean(this.form.withoutTaxes));

    const periodicityOption = (Array.isArray(this.options?.periodicities) ? this.options.periodicities : [])
      .find((item) => String(item?.value || '').trim().toLowerCase() === periodicityCode)
      || null;

    const firstInstallmentOption = (Array.isArray(this.firstInstallmentOptions) ? this.firstInstallmentOptions : [])
      .find((item) => String(item?.value || '').trim() === firstInstallmentValue)
      || null;

    const selectedTax = resolveOption(this.options.taxes || [], taxId);

    this.form = {
      ...this.form,
      saleDate: initial?.sale_date || this.form.saleDate,
      deliveryDate: initial?.delivery_date || this.form.deliveryDate,
      startDate: initial?.start_date || initial?.date || this.form.startDate,
      paymentDate: initial?.payment_date || this.form.paymentDate,
      cfdiDate: initial?.cfdi_date || this.form.cfdiDate,
      code: String(initial?.code || this.form.code || '').trim(),
      customer: (Number(initial?.company_id || initial?.customer_id || 0) > 0)
        ? {
          value: Number(initial?.company_id || initial?.customer_id || 0),
          label: String(initial?.company_name || '').trim() || `Cliente ${Number(initial?.company_id || initial?.customer_id || 0)}`,
        }
        : this.form.customer,
      saleName: String(initial?.name || this.form.saleName || '').trim(),
      service: resolveOption(this.options.services || [], initial?.product_id || initial?.service_id),
      area: resolveOption(this.options.areas || [], initial?.area_id),
      tags: Array.isArray(initial?.tags) ? initial.tags.map((tag) => String(tag || '').trim()).filter(Boolean) : this.form.tags,
      status: resolveOption(this.options.statuses || [], initial?.status_sale_id),
      subtotal: Number(initial?.subtotal ?? initial?.sale_price ?? this.form.subtotal ?? 0) || 0,
      budgetGeneral: Number(initial?.budget_general ?? this.form.budgetGeneral ?? 0) || 0,
      agencyAmount: Number(initial?.agency_amount ?? initial?.sale_import ?? this.form.agencyAmount ?? 0) || 0,
      mediaAmount: Number(initial?.media_amount ?? initial?.advertising_amount ?? this.form.mediaAmount ?? 0) || 0,
      currencyCode: String(initial?.currency || this.form.currencyCode || 'MXN').trim().toUpperCase(),
      exchangeRate: Number(initial?.exchange_rate ?? this.form.exchangeRate ?? 1) || 1,
      manager: resolveOption(this.options.managers || [], initial?.manager_id),
      seller: resolveOption(this.options.sellers || [], initial?.seller_id),
      quoteType: resolveOption(this.options.quoteTypes || [], initial?.quote_type_id || initial?.quote_type?.id),
      quoteMode,
      quoteUrl,
      quoteFile: null,
      quotePrincipal: resolveOption(this.options.quoteCatalogs || [], initial?.quote_id || initial?.quote_principal?.id),
      quoteExtras: quoteExtrasRaw
        .map((item) => resolveOption(this.options.quoteCatalogs || [], item?.id || item))
        .filter((item) => item !== null),
      voucherType: String(initial?.voucher_type || (Number(initial?.special || 0) === 1 ? 'simple_note' : this.form.voucherType || 'cfdi')).trim().toLowerCase(),
      frequency,
      paymentsCount: Math.max(1, Number(initial?.payments_count || initial?.expenses_count || initial?.count_payment || this.form.paymentsCount || 1) || 1),
      firstInstallmentOption,
      periodicityOption,
      withoutTaxes: resolvedWithoutTaxes,
      taxType: taxGroupId > 0 ? 'group' : 'individual',
      taxes: taxGroupId > 0 || !selectedTax ? [] : [selectedTax],
      taxGroup: taxGroupId > 0 ? resolveOption(this.options.taxGroups || [], taxGroupId) : null,
      scope: String(initial?.scope || '').trim(),
      comments: String(initial?.comments || '').trim(),
    };
  },
  findOptionById(optionList = [], rawId = 0) {
    const targetId = Number(rawId || 0);
    if (targetId <= 0) {
      return null;
    }

    return (Array.isArray(optionList) ? optionList : [])
      .find((option) => Number(option?.value || 0) === targetId) || null;
  },
  applyInitialDataToFormMulti() {
    const data = this.initialData && typeof this.initialData === 'object' ? this.initialData : {};

    this.form.code = String(data?.code || this.form.code || '').trim();
    this.form.saleName = String(data?.name || this.form.saleName || '').trim();
    this.form.subtotal = Number(data?.subtotal || data?.sale_price || 0) || 0;
    this.form.budgetGeneral = Number(data?.budget_general || data?.general_budget || 0) || 0;
    this.form.mediaAmount = Number(data?.media_amount || data?.advertising_amount || 0) || 0;
    this.form.agencyAmount = Number(data?.agency_amount || data?.sale_import || 0) || 0;

    this.form.service = this.findOptionById(this.options.services, data?.product_id || data?.service_id);
    this.form.area = this.findOptionById(this.options.areas, data?.area_id);
    this.form.status = this.findOptionById(this.options.statuses, data?.status_sale_id);
    this.form.seller = this.findOptionById(this.options.sellers, data?.seller_id);
    this.form.manager = this.findOptionById(this.options.managers, data?.manager_id);
    this.form.quoteType = this.findOptionById(this.options.quoteTypes, data?.quote_type_id || data?.quote_type?.id);

    const rawCustomerIds = Array.isArray(data?.customer_ids)
      ? data.customer_ids
      : [];

    const customerIds = rawCustomerIds
      .map((id) => Number(id || 0))
      .filter((id) => id > 0);

    if (customerIds.length > 0) {
      const customerItems = Array.isArray(data?.customers) ? data.customers : [];

      this.form.customers = customerIds.map((customerId) => {
        const option = {
          value: customerId,
          label: `Cliente ${customerId}`,
        };

        const customerItem = customerItems.find((item) => Number(item?.id || 0) === customerId);
        if (customerItem) {
          const customerName = String(customerItem?.name || '').trim();
          if (customerName !== '') {
            option.label = customerName;
          }
        }

        return option;
      });

      this.form.customer = this.form.customers[0] || null;
    } else {
      const customerId = Number(data?.customer_id || data?.company_id || 0);
      if (customerId > 0) {
        this.form.customer = {
          value: customerId,
          label: String(data?.company_name || data?.customer_name || 'Cliente').trim() || 'Cliente',
        };
        this.form.customers = [this.form.customer];
      }
    }

    this.form.scope = String(data?.scope || this.form.scope || '').trim();
    this.form.comments = String(data?.comments || this.form.comments || '').trim();
    this.syncAgencyAmount();
  },
  resetState() {
    this.clearTaxPreviewDebounce();
    this.activeSectionKey = this.sections[0].key;
    this.form = this.isMultiCustomerMode
      ? createInitialSaleMultiCustomerForm()
      : createInitialSaleForm();
    this.options = this.isMultiCustomerMode
      ? createInitialSaleMultiCustomerOptions()
      : createInitialSaleOptions();
    this.firstInstallmentOptions = buildFirstInstallmentOptions();
    this.taxPreview = createInitialPaymentPlanTaxPreview();
    this.taxPreviewLoading = false;
    this.taxPreviewRequestToken = 0;
    this.saleCodeRequestToken = 0;
    this.quoteCatalogRequestToken = 0;
    this.quoteCatalogCustomerId = null;
    this.consecutiveType = null;
    this.tagOptions = [];
  },
  resolveInitialCustomerId() {
    const initial = this.initialData && typeof this.initialData === 'object' ? this.initialData : {};
    return toPositiveOptionId(initial?.company_id || initial?.customer_id || 0);
  },
  resolveCurrentCustomerId() {
    return toPositiveOptionId(this.form?.customer);
  },
  syncQuoteSelections() {
    const quoteOptions = Array.isArray(this.options?.quoteCatalogs) ? this.options.quoteCatalogs : [];
    const validValues = new Set(quoteOptions.map((option) => String(option?.value || '').trim()).filter(Boolean));

    const selectedPrincipalValue = String(this.form?.quotePrincipal?.value || '').trim();
    if (selectedPrincipalValue !== '' && !validValues.has(selectedPrincipalValue)) {
      this.form.quotePrincipal = null;
    }

    this.form.quoteExtras = (Array.isArray(this.form?.quoteExtras) ? this.form.quoteExtras : [])
      .filter((option) => validValues.has(String(option?.value || '').trim()));
  },
  resolveDefaultFlatRateTaxOption() {
    const taxes = Array.isArray(this.options?.taxes) ? this.options.taxes : [];
    if (taxes.length === 0) {
      return null;
    }

    const explicitDefault = taxes.find((option) => Number(option?.meta?.default || 0) === 1);
    if (explicitDefault) {
      return explicitDefault;
    }

    const iva16 = taxes.find((option) => {
      const label = String(option?.label || option?.meta?.name || '').trim().toLowerCase();
      const rate = Number(option?.meta?.rate ?? option?.meta?.percentage ?? 0);
      return (label.includes('iva') && label.includes('16')) || rate === 16;
    });

    return iva16 || taxes[0] || null;
  },
  applyFlatRateCreateDefaults() {
    if (!this.isFlatRateMode || this.isMultiCustomerMode || this.isEditMode || this.hasInitialData) {
      return;
    }

    const normalizedFrequency = String(this.form?.frequency || '').trim().toLowerCase();
    if (!['unique', 'monthly', 'multiple'].includes(normalizedFrequency)) {
      this.form.frequency = 'monthly';
    }

    applyFrequencyRules(this.form, this.form.frequency, this.firstInstallmentOptions);

    this.form.voucherType = String(this.form?.voucherType || 'cfdi').trim().toLowerCase() || 'cfdi';
    this.form.startDate = this.form.startDate || buildTodayDate();
    this.form.paymentDate = this.form.paymentDate || buildTodayDate();
    this.form.cfdiDate = this.form.cfdiDate || buildTodayDate();

    if (this.form.voucherType === 'simple_note' || Boolean(this.form?.withoutTaxes)) {
      return;
    }

    const hasSelectedGroup = toPositiveOptionId(this.form?.taxGroup) > 0;
    const hasSelectedTax = (Array.isArray(this.form?.taxes) ? this.form.taxes : []).length > 0;

    if (hasSelectedGroup || hasSelectedTax) {
      return;
    }

    const defaultTax = this.resolveDefaultFlatRateTaxOption();
    if (!defaultTax) {
      return;
    }

    this.form.taxType = 'individual';
    this.form.taxGroup = null;
    this.form.taxes = [defaultTax];
  },
  async loadQuoteCatalogsForCustomer(customerId, { force = false } = {}) {
    const normalizedCustomerId = Number(customerId || 0);

    if (!force && this.quoteCatalogCustomerId === normalizedCustomerId) {
      return;
    }

    this.quoteCatalogCustomerId = normalizedCustomerId;
    const requestToken = this.quoteCatalogRequestToken + 1;
    this.quoteCatalogRequestToken = requestToken;

    if (normalizedCustomerId <= 0) {
      this.options = {
        ...this.options,
        quoteCatalogs: [],
      };
      this.syncQuoteSelections();
      return;
    }

    try {
      const response = await quotesService.selectors(
        { company_id: normalizedCustomerId },
        { skipLoading: true }
      );

      if (requestToken !== this.quoteCatalogRequestToken) {
        return;
      }

      const payload = extractPayload(response);
      const rows = resolveCollectionRows(payload);

      this.options = {
        ...this.options,
        quoteCatalogs: normalizeCatalogOptions(rows, { useCodeInLabel: false }),
      };
      this.syncQuoteSelections();
    } catch (error) {
      if (requestToken !== this.quoteCatalogRequestToken) {
        return;
      }

      this.options = {
        ...this.options,
        quoteCatalogs: [],
      };
      this.syncQuoteSelections();
    }
  },
  async handleOpen() {
    this.resetState();
    await this.loadOptions();

    this.activeSectionKey = this.resolveSectionKey(this.initialSectionKey);

    if (this.isEditMode || this.hasInitialData) {
      this.applyInitialDataToForm();
    }

    this.applyFlatRateCreateDefaults();

    this.scheduleTaxPreview();

    await this.loadQuoteCatalogsForCustomer(this.resolveCurrentCustomerId(), { force: true });

    this.v$.$reset();
  },
  emitClose() {
    this.$emit('close');
  },
  emitDelete() {
    if (!this.isEditMode) {
      return;
    }

    this.$emit('delete', this.initialData || null);
  },
  clearTaxPreviewDebounce() {
    if (!this.taxPreviewDebounceTimer) {
      return;
    }

    clearTimeout(this.taxPreviewDebounceTimer);
    this.taxPreviewDebounceTimer = null;
  },
  scheduleTaxPreview() {
    if (!this.show || !this.isFlatRateMode || this.isMultiCustomerMode) {
      return;
    }

    this.clearTaxPreviewDebounce();
    this.taxPreviewDebounceTimer = setTimeout(() => {
      this.taxPreviewDebounceTimer = null;
      this.loadTaxPreview();
    }, this.taxPreviewDebounceMs);
  },
  buildTaxPreviewPayload() {
    return buildPaymentPlanTaxPreviewPayload({
      amount: Number(this.form?.subtotal || 0) || 0,
      withoutTaxes: Boolean(this.form?.withoutTaxes),
      taxType: this.form?.taxType,
      taxes: this.form?.taxes,
      taxGroup: this.form?.taxGroup,
    });
  },
  async loadTaxPreview() {
    if (!this.show || !this.isFlatRateMode || this.isMultiCustomerMode) {
      return;
    }

    const requestToken = this.taxPreviewRequestToken + 1;
    this.taxPreviewRequestToken = requestToken;
    this.taxPreviewLoading = true;

    try {
      const response = await taxesService.preview(this.buildTaxPreviewPayload(), { skipLoading: true });
      if (requestToken !== this.taxPreviewRequestToken) {
        return;
      }

      const payload = extractPayload(response);
      this.taxPreview = payload && typeof payload === 'object'
        ? payload
        : createInitialPaymentPlanTaxPreview();
    } catch (error) {
      if (requestToken !== this.taxPreviewRequestToken) {
        return;
      }

      this.taxPreview = createInitialPaymentPlanTaxPreview();
    } finally {
      if (requestToken === this.taxPreviewRequestToken) {
        this.taxPreviewLoading = false;
      }
    }
  },
  async loadOptions() {
    this.optionsLoading = true;

    try {
      const initialCustomerId = this.resolveInitialCustomerId();

      const catalogData = this.isMultiCustomerMode
        ? await fetchSaleMultiCustomerModalCatalogs({
          servicesService,
          salesService: salesMultiCustomerService,
          areasService,
          collaboratorsService,
          tagsService,
          quotesService,
          customerId: initialCustomerId,
          isEditMode: this.isEditMode,
        })
        : await fetchSaleModalCatalogs({
          servicesService,
          salesService,
          areasService,
          collaboratorsService,
          tagsService,
          quotesService,
          taxesService,
          generalCatalogsService,
          customerId: initialCustomerId,
          isEditMode: this.isEditMode,
          serviceFilterMode: this.resolvedServiceFilterMode,
          includePaymentPlanCatalogs: this.isFlatRateMode,
        });

      this.options = catalogData.options;
      this.tagOptions = catalogData.tagOptions;
      this.consecutiveType = catalogData.consecutiveType;
      this.form.code = catalogData.initialCode;
      if (this.isMultiCustomerMode) {
        applyDefaultStatusMulti(this.form, this.options.statuses);
      } else {
        applyDefaultStatus(this.form, this.options.statuses);
      }
    } finally {
      this.optionsLoading = false;
    }
  },
  addTag(newTag) {
    const normalized = String(newTag || '').trim();
    if (normalized === '') {
      return;
    }

    const existing = this.tagOptions.find((tag) => String(tag) === normalized);
    if (!existing) {
      this.tagOptions.push(normalized);
    }

    if (!this.form.tags.includes(normalized)) {
      this.form.tags.push(normalized);
    }
  },
  setQuoteMode(mode) {
    const normalized = String(mode || '').trim().toLowerCase();
    if (normalized !== 'url' && normalized !== 'file') {
      return;
    }

    this.form.quoteMode = normalized;
    if (normalized === 'url') {
      this.form.quoteFile = null;
    }

    if (normalized === 'file') {
      this.form.quoteUrl = '';
    }
  },
  onQuoteFileChange(file) {
    this.form.quoteFile = file;
  },
  async loadSuggestedSaleCode(serviceOption) {
    if (this.consecutiveType !== 'services') {
      return;
    }

    const productId = toPositiveOptionId(serviceOption);

    if (productId <= 0) {
      this.form.code = '';
      return;
    }

    const requestToken = this.saleCodeRequestToken + 1;
    this.saleCodeRequestToken = requestToken;

    try {
      const sourceService = this.isMultiCustomerMode ? salesMultiCustomerService : salesService;
      const response = await sourceService.saleCode(
        { product_id: productId },
        { skipLoading: true }
      );
      if (requestToken !== this.saleCodeRequestToken) {
        return;
      }

      const payload = extractPayload(response);
      const code = String(payload?.code || '').trim();
      if (code !== '') {
        this.form.code = code;
      }
    } catch (error) {
      if (requestToken === this.saleCodeRequestToken) {
        this.form.code = '';
      }
    }
  },
  syncAgencyAmount() {
    if (this.isMultiCustomerMode) {
      syncSaleMultiCustomerAgencyAmount(this.form, this.isAdvertisingEnabled);
      return;
    }

    syncSaleAgencyAmount(this.form, this.isAdvertisingEnabled);
  },
  buildPayload() {
    if (this.isMultiCustomerMode) {
      return buildSaleMultiCustomerPayload(this.form, { isAdvertisingEnabled: this.isAdvertisingEnabled });
    }

    return buildSalePayload(this.form, {
      isAdvertisingEnabled: this.isAdvertisingEnabled,
      saleKind: this.isFlatRateMode ? 'flat-rate' : 'business',
      taxPreview: this.taxPreview,
    });
  },
  submitForm() {
    this.v$.$touch();

    if (this.v$.$invalid) {
      this.activeSectionKey = 'general';
      this.focusFirstInvalidField();
      return;
    }

    if (!this.canSubmit) {
      return;
    }

    this.$emit('submit', this.buildPayload());
  },
  resolveFirstInvalidGeneralField() {
    const orderedFields = [
      'saleName',
      'service',
      'customer',
      'customers',
      'paymentsCount',
      'firstInstallmentOption',
      'periodicityOption',
    ];

    for (let index = 0; index < orderedFields.length; index += 1) {
      const field = orderedFields[index];
      if (this.v$?.form?.[field]?.$error) {
        return field;
      }
    }

    return '';
  },
  scrollElementIntoView(target) {
    if (!target || typeof target.scrollIntoView !== 'function') {
      return;
    }

    try {
      target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
    } catch (error) {
      target.scrollIntoView();
    }
  },
  focusFirstInvalidField() {
    const invalidField = this.resolveFirstInvalidGeneralField();
    if (invalidField === '') {
      return;
    }

    this.$nextTick(() => {
      const root = this.$el;
      if (!root || typeof root.querySelector !== 'function') {
        return;
      }

      const selectorMap = {
        saleName: ['input[data-focus-field="saleName"]'],
        service: [
          '[data-focus-field="service"] .multiselect__input',
          '[data-focus-field="service"] .multiselect__tags',
        ],
        customer: [
          '[data-focus-field="customer"] .multiselect__input',
          '[data-focus-field="customer"] .multiselect__tags',
        ],
        customers: [
          '[data-focus-field="customers"] .multiselect__input',
          '[data-focus-field="customers"] .multiselect__tags',
        ],
        paymentsCount: ['input[data-focus-field="paymentsCount"]'],
        firstInstallmentOption: [
          '[data-focus-field="firstInstallmentOption"] .multiselect__input',
          '[data-focus-field="firstInstallmentOption"] .multiselect__tags',
        ],
        periodicityOption: [
          '[data-focus-field="periodicityOption"] .multiselect__input',
          '[data-focus-field="periodicityOption"] .multiselect__tags',
        ],
      };

      const selectors = Array.isArray(selectorMap[invalidField]) ? selectorMap[invalidField] : [];
      for (let index = 0; index < selectors.length; index += 1) {
        const selector = selectors[index];
        const element = root.querySelector(selector);
        if (!element) {
          continue;
        }

        const scrollTarget = element.matches('input, textarea')
          ? element
          : (element.querySelector('input, textarea') || element);

        this.scrollElementIntoView(scrollTarget);
        return;
      }
    });
  },
};
